import { useState, useCallback, useMemo, type FormEvent } from 'react';
import { apiClient } from '@/api/client';

interface DatabaseModalProps {
  connectionId: string;
  mode: 'create' | 'edit';
  currentName?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function DatabaseModal({ connectionId, mode, currentName, onClose, onSaved }: DatabaseModalProps) {
  const [name, setName] = useState(currentName ?? '');
  const [encoding, setEncoding] = useState('UTF8');
  const [newName, setNewName] = useState(currentName ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isCreate = mode === 'create';
  const title = isCreate ? 'Create Database' : `Edit Database — ${currentName}`;

  const previewSQL = useMemo(() => {
    if (isCreate) {
      const enc = encoding && encoding !== 'UTF8' ? ` ENCODING '${encoding}'` : '';
      return `CREATE DATABASE "${name}"${enc};`;
    }
    if (currentName && newName && newName !== currentName) {
      return `ALTER DATABASE "${currentName}" RENAME TO "${newName}";`;
    }
    return '-- No changes';
  }, [isCreate, name, encoding, currentName, newName]);

  const canSave = isCreate
    ? name.trim().length > 0
    : newName.trim().length > 0 && newName !== currentName;

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!canSave) return;

    setLoading(true);
    setError('');

    try {
      if (isCreate) {
        await apiClient.post(`/api/management/${connectionId}/create-database`, {
          name: name.trim(),
          encoding: encoding || undefined,
        });
      } else if (newName !== currentName) {
        // Rename database via raw SQL
        await apiClient.post('/api/query/execute', {
          connectionId,
          sql: `ALTER DATABASE "${currentName}" RENAME TO "${newName.trim()}"`,
        });
      }
      onSaved();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Operation failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [canSave, isCreate, connectionId, name, encoding, newName, currentName, onSaved]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        className="w-[460px] bg-dbgo-modal border rounded-lg shadow-2xl flex flex-col max-h-[80vh]"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-subtle">
          <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {isCreate ? (
            <>
              <div>
                <label className="block text-xs font-semibold text-text-secondary mb-1.5">
                  Database Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="my_database"
                  autoFocus
                  className="w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-text-secondary mb-1.5">
                  Encoding
                </label>
                <select
                  value={encoding}
                  onChange={(e) => setEncoding(e.target.value)}
                  className="w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary outline-none focus:border-focus transition-colors"
                >
                  <option value="UTF8">UTF8 (default)</option>
                  <option value="LATIN1">LATIN1</option>
                  <option value="SQL_ASCII">SQL_ASCII</option>
                  <option value="WIN1252">WIN1252</option>
                </select>
              </div>
            </>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1.5">
                Rename To
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
                className="w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors"
              />
            </div>
          )}

          {/* SQL Preview */}
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">
              SQL Preview
            </label>
            <pre className="bg-dbgo-editor border rounded-sm px-3 py-2 text-xs font-mono text-text-primary whitespace-pre-wrap overflow-x-auto max-h-[80px]">
              {previewSQL}
            </pre>
          </div>

          {error && (
            <div className="text-xs text-accent-red">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-subtle flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave || loading}
            className="px-4 py-1.5 text-xs font-medium bg-accent-blue hover:bg-accent-blue/90 text-white rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (isCreate ? 'Creating...' : 'Saving...') : (isCreate ? 'Create' : 'Save')}
          </button>
        </div>
      </form>
    </div>
  );
}
